
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useEffect, useState, useMemo } from "react";
import { getWalletTransactions } from "../../../services/transactionService";
import { Transaction } from "../../../types";
import { useWallet } from "../../../hooks/useWallet";
import { Loader2, ArrowUpRight, ArrowDownLeft, Clock, ExternalLink, CheckCircle, AlertCircle, Repeat, Shield } from "lucide-react";
import { ChainIcon, TokenIcon } from "../../ui/ChainIcons";

export const WalletTransactionList: React.FC<{ walletAddress: string, limit?: number }> = ({ walletAddress, limit = 5 }) => {
  const [txs, setTxs] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { balances } = useWallet();
  
  // Map current balance tokens for real-time price lookups (via Pyth)
  const priceMap = useMemo(() => {
    const map = new Map<string, number>();
    balances.forEach(b => map.set(b.symbol, b.priceUsd));
    return map;
  }, [balances]);

  useEffect(() => {
    async function loadTxs() {
      if (!walletAddress) return;
      setIsLoading(true);
      try {
        const { transactions } = await getWalletTransactions(walletAddress, 1, limit);
        
        // Enrich transactions with USD values based on the latest available oracle price
        const enrichedTxs = transactions.map(tx => ({
            ...tx,
            usdAmount: tx.amount * (priceMap.get(tx.asset) || 1)
        }));

        setTxs(enrichedTxs);
      } catch (e) {
          console.error("Ledger synchronization error:", e);
      } finally {
          setIsLoading(false);
      }
    }
    loadTxs();
  }, [walletAddress, limit, priceMap]);

  const getTxTypeDetails = (type: Transaction['type']) => {
    switch (type) {
        case 'swap': return { icon: Repeat, color: 'text-indigo-500' };
        case 'escrow': return { icon: Shield, color: 'text-amber-500' };
        case 'payment': return { icon: ArrowUpRight, color: 'text-red-500' };
        case 'deposit': return { icon: ArrowDownLeft, color: 'text-emerald-500' };
        default: return { icon: ArrowUpRight, color: 'text-stone-400' };
    }
  };
  
  const isOutflow = (type: Transaction['type']) => ['payment', 'withdrawal', 'swap', 'lock'].includes(type);

  const getStatusBadge = (status: Transaction['status']) => {
      switch(status) {
          case 'completed': return <div className="flex items-center gap-1.5 text-[9px] font-black text-emerald-500 uppercase tracking-widest bg-emerald-500/5 px-2 py-0.5 rounded-sm border border-emerald-500/10"><CheckCircle size={10}/> Confirmed</div>;
          case 'pending': return <div className="flex items-center gap-1.5 text-[9px] font-black text-amber-500 uppercase tracking-widest bg-amber-500/5 px-2 py-0.5 rounded-sm border border-amber-500/10 animate-pulse"><Clock size={10}/> Syncing</div>;
          case 'failed': return <div className="flex items-center gap-1.5 text-[9px] font-black text-red-500 uppercase tracking-widest bg-red-500/5 px-2 py-0.5 rounded-sm border border-red-500/10"><AlertCircle size={10}/> Dropped</div>;
          default: return null;
      }
  };

  if (isLoading) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center text-stone-400 space-y-4 py-20">
            <Loader2 size={32} className="animate-spin text-nobel-gold" />
            <span className="text-[10px] font-black uppercase tracking-[0.3em] animate-pulse">Synchronizing Global Ledger...</span>
        </div>
      );
  }
  
  if (txs.length === 0) {
      return (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-12 opacity-40">
              <Clock size={48} strokeWidth={1} className="mb-4" />
              <p className="text-sm font-black uppercase tracking-widest">No recent network activity</p>
          </div>
      );
  }

  return (
    <div className="flex-1 space-y-1">
        {txs.map(tx => {
            const isOut = isOutflow(tx.type);
            const { icon: TypeIcon, color: typeColor } = getTxTypeDetails(tx.type);
            
            return (
              <div key={tx.id} className="group flex items-center gap-4 p-4 rounded-sm hover:bg-stone-50 dark:hover:bg-white/[0.03] border border-transparent hover:border-stone-100 dark:hover:border-white/5 transition-all cursor-pointer">
                  {/* Ledger Logic Column */}
                  <div className="relative shrink-0">
                    <div className="w-12 h-12 rounded-sm bg-stone-100 dark:bg-white/5 flex items-center justify-center border border-stone-200 dark:border-white/10 shadow-inner group-hover:scale-105 transition-transform duration-300">
                        <TokenIcon symbol={tx.asset} className="w-7 h-7" />
                    </div>
                    <div className="absolute -bottom-1 -right-1 bg-white dark:bg-[#15171C] rounded-full p-0.5 border border-stone-100 dark:border-white/10 shadow-sm">
                        <ChainIcon chain={tx.chain} className="w-4 h-4" />
                    </div>
                  </div>

                  {/* Context Column */}
                  <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-black text-[#0A0B0F] dark:text-stone-200 uppercase tracking-tighter truncate">{tx.type}</span>
                        {getStatusBadge(tx.status)}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                         <span className="text-[10px] text-stone-400 font-mono tracking-tight">{new Date(tx.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                         <span className="text-stone-200 dark:text-stone-800">|</span>
                         <span className="text-[10px] text-stone-500 font-mono truncate max-w-[120px] hover:text-nobel-gold transition-colors">{tx.txHash.slice(0, 14)}...</span>
                      </div>
                  </div>

                  {/* Valuation Column (Pyth-Integrated) */}
                  <div className="text-right">
                      <div className={`font-mono text-sm font-bold ${
                          isOut ? 'text-stone-900 dark:text-white' : 'text-emerald-500'
                      }`}>
                        {isOut ? '-' : '+'}{tx.amount.toLocaleString(undefined, { maximumFractionDigits: 4 })} {tx.asset}
                      </div>
                      {tx.usdAmount !== undefined && (
                        <div className="text-[10px] text-stone-400 font-black tracking-widest mt-0.5">
                            ${tx.usdAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span className="opacity-50 font-sans">USD</span>
                        </div>
                      )}
                  </div>
                  
                  {/* Action Column */}
                  <div className="hidden sm:flex opacity-0 group-hover:opacity-100 transition-opacity ml-2">
                      <a 
                        href={`${tx.explorerUrl}${tx.txHash}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="p-2 text-stone-400 hover:text-nobel-gold bg-white dark:bg-white/5 rounded-sm border border-stone-100 dark:border-white/10 shadow-sm transition-all"
                        onClick={(e) => e.stopPropagation()}
                      >
                          <ExternalLink size={14} />
                      </a>
                  </div>
              </div>
            )
        })}
    </div>
  );
};
